import { serializePausePeriods, type HabitChangesResponse } from '@fasting-timer/shared'

import type { Habit } from '../state/habitsStore'
import {
   createDeltaSyncRunner,
   readStoredObject,
   resolveSyncStorage,
   writeStoredObject,
   type SyncScheduler,
   type SyncStorage,
} from './deltaSyncRunner'
import {
   HabitWriteRejectedError,
   type HabitsApi,
   type HabitUpsertBody,
   type HabitWriteResult,
} from './habitsApi'

const STATE_STORAGE_KEY = 'habitsSyncState'

/** A habit as of the last reconcile: its synced fields and its completions (epoch ms). */
type KnownHabit = { fields: string; completions: number[] }

/**
 * Local changes the server has not confirmed. `upsert` is the habit's
 * `updatedAt` when its fields changed; `deleteAt` replaces everything else.
 */
type PendingHabit = { upsert?: number; completions?: number[]; deleteAt?: number }

/**
 * Persisted sync bookkeeping. `known` is diffed against the local list to find
 * edits; `init` seeds it at startup. A null `cursor` means the first full pull
 * (which merges without inferring deletes) has not happened; `needsFullPull`
 * asks for a full re-read after a write turned out stale, since the newer
 * server version may predate the cursor.
 */
type HabitsSyncState = {
   cursor: string | null
   known: Record<string, KnownHabit> | null
   pending: Record<string, PendingHabit>
   needsFullPull: boolean
}

type HabitsSyncDeps = {
   api: Pick<HabitsApi, 'changes' | 'upsert' | 'complete' | 'remove'>
   getLocalHabits: () => readonly Habit[]
   /** Builds a full local habit from a server record (schedule normalization lives in the store). */
   toLocalHabit: (record: Record<string, unknown>) => Habit | null
   /** Replaces the local list after server changes, recording deletions as local tombstones. */
   setLocalHabits: (habits: Habit[], deleted: Record<string, number>) => void
   storage?: SyncStorage
   schedule?: SyncScheduler
   now?: () => number
}

/**
 * Returns a fresh, never-synced state.
 *
 * @returns The empty state.
 */
function emptyState(): HabitsSyncState {
   return { cursor: null, known: null, pending: {}, needsFullPull: false }
}

/**
 * Reads stored sync state, falling back to an empty state on any mismatch.
 *
 * @param storage - The storage, or null.
 * @returns The state.
 */
function readState(storage: SyncStorage | null): HabitsSyncState {
   const value = readStoredObject(
      storage,
      STATE_STORAGE_KEY,
   ) as Partial<HabitsSyncState> | null
   if (!value) {
      return emptyState()
   }
   return {
      cursor: typeof value.cursor === 'string' ? value.cursor : null,
      known: value.known && typeof value.known === 'object' ? value.known : null,
      pending: value.pending && typeof value.pending === 'object' ? value.pending : {},
      needsFullPull: value.needsFullPull === true,
   }
}

/**
 * Serializes the fields `PUT /v1/habits/:id` carries, for change detection.
 *
 * @param habit - The habit.
 * @returns A comparable key.
 */
function fieldsKey(habit: Habit) {
   return JSON.stringify([
      habit.name,
      habit.kind,
      habit.scheduleDays,
      habit.dueDate,
      habit.scheduleRule,
      habit.paused,
      habit.pausePeriods,
      [...habit.alsoCompletes].sort(),
      habit.tags,
   ])
}

/**
 * Captures a habit as the last-reconciled state.
 *
 * @param habit - The habit.
 * @returns The known record.
 */
function toKnown(habit: Habit): KnownHabit {
   return { fields: fieldsKey(habit), completions: [...habit.completionHistory] }
}

/**
 * Builds the upsert body for a habit.
 *
 * @param habit - The local habit.
 * @returns The request body.
 */
function toUpsertBody(habit: Habit): HabitUpsertBody {
   return {
      name: habit.name,
      kind: habit.kind,
      scheduleDays: habit.scheduleDays,
      scheduleRule: habit.scheduleRule ?? '',
      dueDate: habit.dueDate === null ? null : new Date(habit.dueDate).toISOString(),
      paused: habit.paused,
      pausePeriods: serializePausePeriods(habit.pausePeriods),
      alsoCompletes: habit.alsoCompletes,
      tags: habit.tags,
      updatedAt: new Date(habit.updatedAt).toISOString(),
   }
}

/**
 * Queues the differences between the local list and `known`: field edits,
 * new completions, and deletions.
 *
 * @param state - The sync state (mutated).
 * @param local - The local habits.
 * @param nowMs - The deletion time for removed habits.
 */
function queueLocalChanges(
   state: HabitsSyncState,
   local: readonly Habit[],
   nowMs: number,
) {
   const known = state.known ?? {}
   const localIds = new Set<string>()
   for (const habit of local) {
      localIds.add(habit.id)
      const before = known[habit.id]
      const pending = state.pending[habit.id] ?? {}
      if (!before || before.fields !== fieldsKey(habit)) {
         pending.upsert = habit.updatedAt
      }
      const seen = new Set(before?.completions ?? [])
      const added = habit.completionHistory.filter(ms => !seen.has(ms))
      if (added.length > 0) {
         pending.completions = [...new Set([...(pending.completions ?? []), ...added])]
      }
      if (pending.upsert !== undefined || pending.completions) {
         delete pending.deleteAt
         state.pending[habit.id] = pending
      }
   }
   for (const id of Object.keys(known)) {
      if (!localIds.has(id)) {
         state.pending[id] = { deleteAt: nowMs }
      }
   }
   state.known = Object.fromEntries(local.map(habit => [habit.id, toKnown(habit)]))
}

/**
 * Applies a server habit tombstone unless a newer local edit re-created the habit.
 *
 * @param state - The sync state (mutated).
 * @param local - The local habits by id (mutated).
 * @param id - The habit id.
 * @param deletedAtMs - The server's deletion time.
 * @returns The deletion time when the habit was removed locally, else null.
 */
function mergeHabitTombstone(
   state: HabitsSyncState,
   local: Map<string, Habit>,
   id: string,
   deletedAtMs: number,
) {
   const pendingUpsert = state.pending[id]?.upsert
   if (pendingUpsert !== undefined && pendingUpsert > deletedAtMs) {
      return null
   }
   delete state.pending[id]
   return local.delete(id) ? deletedAtMs : null
}

/**
 * Returns whether a pending local change outranks a server habit version: a
 * pending delete always does, a pending edit when it is at least as new.
 *
 * @param pending - The habit's pending change, if any.
 * @param existing - The local habit, if any.
 * @param remoteUpdatedMs - The server version's edit time.
 * @returns True when the local version is kept.
 */
function localVersionWins(
   pending: PendingHabit | undefined,
   existing: Habit | undefined,
   remoteUpdatedMs: number,
) {
   if (!pending) {
      return false
   }
   if (pending.deleteAt !== undefined) {
      return true
   }
   return pending.upsert !== undefined && (existing?.updatedAt ?? 0) >= remoteUpdatedMs
}

/**
 * Applies one server habit change to the local map, honoring newer pending edits.
 *
 * @param state - The sync state (mutated).
 * @param local - The local habits by id (mutated).
 * @param change - The server habit.
 * @param deps - The record normalizer.
 * @returns The deletion time when the habit was removed locally, else null.
 */
function mergeHabitChange(
   state: HabitsSyncState,
   local: Map<string, Habit>,
   change: HabitChangesResponse['habits'][number],
   deps: Pick<HabitsSyncDeps, 'toLocalHabit'>,
) {
   if (change.deletedAt) {
      return mergeHabitTombstone(state, local, change.id, Date.parse(change.deletedAt))
   }
   const pending = state.pending[change.id]
   const existing = local.get(change.id)
   if (localVersionWins(pending, existing, Date.parse(change.updatedAt))) {
      return null
   }
   if (pending) {
      delete pending.upsert
   }
   const remoteLastMs = change.lastCompletedAt ? Date.parse(change.lastCompletedAt) : 0
   const merged = deps.toLocalHabit({
      ...change,
      completionHistory: existing?.completionHistory ?? [],
      lastCompletedAt: Math.max(existing?.lastCompletedAt ?? 0, remoteLastMs) || null,
   })
   if (merged) {
      local.set(change.id, merged)
   }
   return null
}

/**
 * Applies server completion changes to the local habits, keeping completions
 * this device has not pushed yet.
 *
 * @param state - The sync state.
 * @param local - The local habits by id (mutated).
 * @param completions - The server completion changes.
 */
function mergeCompletionChanges(
   state: HabitsSyncState,
   local: Map<string, Habit>,
   completions: HabitChangesResponse['completions'],
) {
   for (const change of completions) {
      const habit = local.get(change.habitId)
      const completedMs = Date.parse(change.completedAt)
      if (!habit || !Number.isFinite(completedMs)) {
         continue
      }
      const history = new Set(habit.completionHistory)
      const pendingHere =
         state.pending[change.habitId]?.completions?.includes(completedMs)
      if (change.deletedAt && !pendingHere) {
         history.delete(completedMs)
      } else if (!change.deletedAt) {
         history.add(completedMs)
      }
      const completionHistory = [...history].sort((a, b) => a - b)
      if (completionHistory.length !== habit.completionHistory.length) {
         local.set(change.habitId, {
            ...habit,
            completionHistory,
            lastCompletedAt: completionHistory.at(-1) ?? null,
         })
      }
   }
}

/**
 * Applies a changes response: habits first (so new habits can take their
 * completions), then completions; records the result as `known`.
 *
 * @param state - The sync state (mutated).
 * @param response - The server changes.
 * @param deps - Local-list accessors.
 */
function applyChanges(
   state: HabitsSyncState,
   response: HabitChangesResponse,
   deps: Pick<HabitsSyncDeps, 'getLocalHabits' | 'setLocalHabits' | 'toLocalHabit'>,
) {
   const before = deps.getLocalHabits()
   const local = new Map(before.map(habit => [habit.id, habit]))
   const deleted: Record<string, number> = {}
   for (const change of response.habits) {
      const deletedAtMs = mergeHabitChange(state, local, change, deps)
      if (deletedAtMs !== null) {
         deleted[change.id] = deletedAtMs
      }
   }
   mergeCompletionChanges(state, local, response.completions)
   const known = state.known ?? {}
   for (const id of Object.keys(deleted)) {
      delete known[id]
   }
   for (const habit of local.values()) {
      if (before.find(entry => entry.id === habit.id) !== habit) {
         known[habit.id] = toKnown(habit)
      }
   }
   state.known = known
   const changed =
      Object.keys(deleted).length > 0 ||
      local.size !== before.length ||
      before.some(habit => local.get(habit.id) !== habit)
   if (changed) {
      deps.setLocalHabits([...local.values()], deleted)
   }
}

/**
 * First full pull: queues local habits and completions the server has never
 * seen. Local habits are never read as deletes here.
 *
 * @param state - The sync state (mutated).
 * @param local - The local habits.
 * @param response - The server's full changes list.
 */
function queueLocalOnly(
   state: HabitsSyncState,
   local: readonly Habit[],
   response: HabitChangesResponse,
) {
   const remoteIds = new Set(response.habits.map(change => change.id))
   const remoteCompletions = new Set(
      response.completions.map(
         change => `${change.habitId}|${Date.parse(change.completedAt)}`,
      ),
   )
   for (const habit of local) {
      const pending = state.pending[habit.id] ?? {}
      if (!remoteIds.has(habit.id) && pending.upsert === undefined) {
         pending.upsert = habit.updatedAt
      }
      const unseen = habit.completionHistory.filter(
         ms => !remoteCompletions.has(`${habit.id}|${ms}`),
      )
      if (unseen.length > 0) {
         pending.completions = [...new Set([...(pending.completions ?? []), ...unseen])]
      }
      if (pending.upsert !== undefined || pending.completions) {
         state.pending[habit.id] = pending
      }
   }
}

/**
 * Runs a write, mapping an invalid-request refusal to `'rejected'` so the
 * change is dropped instead of retried forever.
 *
 * @param write - The write.
 * @returns The outcome, `'rejected'`, or null when remote sync is unavailable.
 */
async function runWrite(
   write: () => Promise<HabitWriteResult | null>,
): Promise<HabitWriteResult | 'rejected' | null> {
   try {
      return await write()
   } catch (error) {
      if (error instanceof HabitWriteRejectedError) {
         console.warn('Habit change rejected by the server', error.message)
         return 'rejected'
      }
      throw error
   }
}

/**
 * Pushes a pending delete and clears it unless a newer delete replaced it.
 *
 * @param state - The sync state (mutated).
 * @param id - The habit id.
 * @param deleteAt - The deletion time that was sent.
 * @param api - The habits API.
 * @returns False when remote sync became unavailable.
 */
async function pushDelete(
   state: HabitsSyncState,
   id: string,
   deleteAt: number,
   api: HabitsSyncDeps['api'],
) {
   const result = await runWrite(() => api.remove(id))
   if (result === null) {
      return false
   }
   if (state.pending[id]?.deleteAt === deleteAt) {
      delete state.pending[id]
   }
   return true
}

/**
 * Pushes a habit's fields; a stale answer asks for a full re-read.
 *
 * @param state - The sync state (mutated).
 * @param habit - The local habit.
 * @param upsert - The pending edit's `updatedAt` that was sent.
 * @param api - The habits API.
 * @returns False when remote sync became unavailable (or the user is unknown).
 */
async function pushUpsert(
   state: HabitsSyncState,
   habit: Habit,
   upsert: number,
   api: HabitsSyncDeps['api'],
) {
   const result = await runWrite(() => api.upsert(habit.id, toUpsertBody(habit)))
   if (result === null || result === 'missing') {
      return false
   }
   state.needsFullPull ||= result === 'stale'
   const pending = state.pending[habit.id]
   if (pending?.upsert === upsert) {
      delete pending.upsert
   }
   return true
}

/**
 * Pushes new completions through the complete endpoint, which never drops
 * them as stale, clearing each one once answered.
 *
 * @param state - The sync state (mutated).
 * @param id - The habit id.
 * @param completions - The completion times (epoch ms) that were pending.
 * @param api - The habits API.
 * @returns False when remote sync became unavailable.
 */
async function pushCompletions(
   state: HabitsSyncState,
   id: string,
   completions: readonly number[],
   api: HabitsSyncDeps['api'],
) {
   for (const completedMs of completions) {
      const result = await runWrite(() =>
         api.complete(id, new Date(completedMs).toISOString()),
      )
      if (result === null) {
         return false
      }
      const pending = state.pending[id]
      if (pending?.completions) {
         pending.completions = pending.completions.filter(ms => ms !== completedMs)
      }
   }
   return true
}

/**
 * Pushes one habit's pending change: a delete, or an upsert followed by its
 * new completions.
 *
 * @param state - The sync state (mutated).
 * @param id - The habit id.
 * @param deps - The API and local-list accessor.
 * @returns False when remote sync became unavailable.
 */
async function pushHabit(
   state: HabitsSyncState,
   id: string,
   deps: Pick<HabitsSyncDeps, 'api' | 'getLocalHabits'>,
) {
   const sent = { ...state.pending[id] }
   if (sent.deleteAt !== undefined) {
      return pushDelete(state, id, sent.deleteAt, deps.api)
   }
   const habit = deps.getLocalHabits().find(entry => entry.id === id)
   if (!habit) {
      return true
   }
   const reachable =
      (sent.upsert === undefined ||
         (await pushUpsert(state, habit, sent.upsert, deps.api))) &&
      (await pushCompletions(state, id, sent.completions ?? [], deps.api))
   const pending = state.pending[id]
   if (pending && pending.upsert === undefined && !pending.completions?.length) {
      delete state.pending[id]
   }
   return reachable
}

/**
 * Creates the habits delta-sync runtime: diffs local edits into per-habit
 * writes on the `/v1/habits` routes and pulls `GET /v1/habits/changes` since
 * the last `serverTime`.
 *
 * @param deps - Local-list accessors, the API client, and optional storage/scheduler.
 * @returns The sync methods.
 */
export function createHabitsSync(deps: HabitsSyncDeps) {
   const storage = resolveSyncStorage(deps.storage)
   const now = deps.now ?? Date.now
   let state = readState(storage)

   /**
    * Pulls changes: a full read on bootstrap or after a stale write, else since the cursor.
    *
    * @returns True when the server was reachable.
    */
   async function pull() {
      const full = state.cursor === null || state.needsFullPull
      const response = await deps.api.changes({ since: full ? null : state.cursor })
      if (!response) {
         return false
      }
      if (state.cursor === null) {
         queueLocalOnly(state, deps.getLocalHabits(), response)
      }
      applyChanges(state, response, deps)
      state.cursor = response.serverTime
      state.needsFullPull = false
      writeStoredObject(storage, STATE_STORAGE_KEY, state)
      return true
   }

   /**
    * Pushes every pending habit change, persisting after each habit.
    */
   async function push() {
      for (const id of Object.keys(state.pending)) {
         const reachable = await pushHabit(state, id, deps)
         writeStoredObject(storage, STATE_STORAGE_KEY, state)
         if (!reachable) {
            return
         }
      }
   }

   const runner = createDeltaSyncRunner({
      needsBootstrap: () => state.cursor === null,
      pull,
      push,
      label: 'habits',
      schedule: deps.schedule,
   })

   return {
      sync: runner.sync,
      /**
       * Returns whether a sync has something to do without a server change:
       * a first pull, a requested full re-read, or unpushed local edits.
       *
       * @returns True when the next sync should run regardless of the change stamp.
       */
      hasLocalWork() {
         return (
            state.cursor === null ||
            state.needsFullPull ||
            Object.keys(state.pending).length > 0
         )
      },
      /**
       * Seeds `known` from the current local list when it is unset, so later
       * edits (including deletes made before the first sync) can be diffed, and
       * otherwise queues any drift between the stored list and `known`. Call
       * after the stored habits are loaded.
       */
      init() {
         // Re-read: another tab may have written newer bookkeeping since creation.
         state = readState(storage)
         const local = deps.getLocalHabits()
         if (state.known === null) {
            state.known = Object.fromEntries(
               local.map(habit => [habit.id, toKnown(habit)]),
            )
         } else {
            // Re-queues any local change whose pending record another tab overwrote.
            queueLocalChanges(state, local, now())
         }
         writeStoredObject(storage, STATE_STORAGE_KEY, state)
      },
      /**
       * Diffs the local list against `known`, queues the differences, and
       * schedules a push.
       */
      recordLocalChange() {
         queueLocalChanges(state, deps.getLocalHabits(), now())
         writeStoredObject(storage, STATE_STORAGE_KEY, state)
         runner.schedulePush()
      },
      /**
       * Forgets all sync state so the next sync bootstraps (key change / reset).
       */
      reset() {
         runner.cancel()
         state = emptyState()
         writeStoredObject(storage, STATE_STORAGE_KEY, null)
      },
   }
}
